import React from 'react'
import { cn } from './cn'

export type SegmentedControlValue = string | number

export interface SegmentedControlOption<Value extends SegmentedControlValue> {
  value: Value
  label: React.ReactNode
  disabled?: boolean
}

export type SegmentedControlVariant = 'pill' | 'compact' | 'ink' | 'tiles' | 'unstyled'

interface SegmentedControlBaseProps<Value extends SegmentedControlValue> {
  value: Value
  options: readonly SegmentedControlOption<Value>[]
  onValueChange: (value: Value) => void
  disabled?: boolean
  variant?: SegmentedControlVariant
  className?: string
}

type SegmentedControlAccessibleName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-label'?: never; 'aria-labelledby': string }

export type SegmentedControlProps<Value extends SegmentedControlValue> =
  SegmentedControlBaseProps<Value> & SegmentedControlAccessibleName

/**
 * Mutually-exclusive button switch used throughout the Web app.
 *
 * This is intentionally separate from the binary checkbox-based `Switch` and
 * from `ViewTabs`, which owns tab/panel keyboard semantics. `variant="unstyled"`
 * keeps the shared behavior while allowing a context such as the transcript
 * player to retain its established geometry. `variant="tiles"` is the App
 * surface's flat single choice (design `.lang`): equal outlined tiles side by
 * side, the chosen one ink with white text, used for a setting row's control.
 */
export function SegmentedControl<Value extends SegmentedControlValue>({
  value,
  options,
  onValueChange,
  disabled = false,
  variant = 'pill',
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SegmentedControlProps<Value>) {
  const variantClass =
    variant === 'unstyled'
      ? ''
      : variant === 'tiles'
        ? 'inline-grid auto-cols-fr grid-flow-col overflow-hidden rounded-[var(--radius)] border border-border'
        : variant === 'compact'
          ? 'rounded-[var(--radius)] border border-border bg-background p-0.5 text-xs'
          : variant === 'ink'
            ? 'h-[var(--ctl-h)] rounded-full border border-border bg-transparent p-[3px] text-md'
            : // Single-line controls share --ctl-h, so a segmented control sitting in
              // a form row lines up with the inputs beside it. The pre-shared-component
              // `.bill-toggle` pinned the same height; dropping it left the Team
              // checkout's cycle toggle 1.4px shorter than its neighbours.
              'h-[var(--ctl-h)] rounded-full border border-border bg-surface p-[3px] text-sm'

  return (
    <div
      className={cn(
        variant !== 'tiles' && 'inline-flex items-center gap-0.5',
        variantClass,
        className
      )}
      role="group"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      data-ui="segmented-control"
    >
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          aria-pressed={option.value === value}
          disabled={disabled || option.disabled}
          className={cn(
            variant === 'tiles'
              ? 'h-8.5 whitespace-nowrap border-border px-3.5 text-sm text-muted transition-colors not-first:border-l hover:bg-[var(--fg-soft)] hover:text-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-40'
              : variant === 'ink'
                ? 'h-[30px] rounded-full px-3.5 py-0 text-sm leading-[1.65] text-muted transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] disabled:cursor-not-allowed disabled:opacity-40'
                : 'rounded-[calc(var(--radius)-2px)] px-3 py-1.5 text-inherit text-muted transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] disabled:cursor-not-allowed disabled:opacity-40',
            variant === 'pill' && 'rounded-full',
            option.value === value &&
              variant !== 'unstyled' &&
              variant !== 'ink' &&
              variant !== 'tiles' &&
              'bg-background font-medium text-foreground shadow-sm',
            option.value === value &&
              variant === 'tiles' &&
              'bg-foreground font-semibold text-background hover:bg-foreground hover:text-background',
            option.value === value &&
              variant === 'ink' &&
              'bg-foreground font-medium text-background hover:text-background',
            option.value === value && variant === 'unstyled' && 'font-semibold text-foreground'
          )}
          onClick={() => onValueChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
